/* eslint-disable @typescript-eslint/no-explicit-any */
import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useIsMobile } from "@/hooks/use-mobile";
import { convertToEuro } from "@/libs/amount";
import { calculReporting } from "@/libs/utils";
import { VisibilityState } from "@tanstack/react-table";
import React, { useState } from "react";
import SummaryColumn from "./SummaryColumn";
const Totals = ({
  columnVisibility,
  rowSelections,
  setRowSelections,
  projectsByPp,
  dataReport,
}: {
  columnVisibility: VisibilityState;
  rowSelections: number[];
  setRowSelections: React.Dispatch<React.SetStateAction<number[]>>;
  projectsByPp: Record<string, any>;
  dataReport: any;
}) => {
  const [filteredTotals, setFilteredTotals] = React.useState({
    obtained: 0,
    contributions: 0,
    treesPlanted: 0,
    regeneratedAreas: 0,
    co2Sequestered: 0,
    vegetationCoverage: 0,
    jobsCreated: 0,
  });
  const [loading, setLoading] = useState(false);
  const updateFilteredData = React.useCallback(async () => {
    if (!Array.isArray(rowSelections) || rowSelections.length === 0) {
      setFilteredTotals({
        obtained: 0,
        contributions: 0,
        treesPlanted: 0,
        regeneratedAreas: 0,
        co2Sequestered: 0,
        vegetationCoverage: 0,
        jobsCreated: 0,
      });
      return;
    }

    setLoading(true);

    const selectedProjects = Object.values(projectsByPp)
      .flatMap((program) => (program as { projects: any[] }).projects)
      .filter((project) => rowSelections?.map(Number).includes(project.id));

    const desiredFundingConversions = selectedProjects.flatMap((project) =>
      project.desiredFunding.map((fund) =>
        convertToEuro(fund.amount, fund.devise)
      )
    );
    const obtainedFundingConversions = selectedProjects.flatMap((project) =>
      project.obtainedFunding.map((fund) =>
        convertToEuro(fund.amount, fund.devise)
      )
    );

    const [desiredResults, obtainedResults] = await Promise.all([
      Promise.all(desiredFundingConversions),
      Promise.all(obtainedFundingConversions),
    ]);

    const totalDesiredFunding = desiredResults.reduce(
      (acc, val) => acc + val,
      0
    );
    const totalObtainedFunding = obtainedResults.reduce(
      (acc, val) => acc + val,
      0
    );

    let reporting = null;
    if (dataReport) {
      reporting = calculReporting(dataReport, null, selectedProjects);
    }

    const getTotal = (code: string) => {
      return (
        reporting?.projectTotalIndicators?.find(
          (pti) => pti.indicator?.code === code && pti.type === 1
        )?.total || 0
      );
    };

    const fees = totalDesiredFunding * 0.15;
    const callContributions = totalDesiredFunding + fees;

    setFilteredTotals({
      obtained: totalObtainedFunding, // Si vous voulez inclure ça ici
      contributions: callContributions, // Si vous voulez inclure ça ici
      co2Sequestered: getTotal("tco2_sequestrees"), // Utilisation directe de getTotal
      treesPlanted: getTotal("arbres_plantes"),
      regeneratedAreas: getTotal("surface_regeneree"),
      vegetationCoverage: getTotal("couverture_vegetale"),
      jobsCreated: getTotal("creation_d_emploi"),
    });
    setLoading(false);
  }, [projectsByPp, rowSelections, dataReport]);
  const isMobile = useIsMobile();
  React.useEffect(() => {
    updateFilteredData();
  }, [updateFilteredData]);
  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="fixed bottom-0 z-50 h-14 bg-gray-100 pr-4 pl-10 flex gap-2 items-center right-0 w-full md:w-[calc(100%-301px)] ">
      <ScrollArea className="w-full">
        <table className="w-[80%] mr-auto caption-bottom text-sm">
          <thead>
            <tr className="border-b ">
              {rowSelections.length > 0 && (
                <th className="h-12 text-left align-middle font-medium text-muted-foreground">
                  <div
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 underline-offset-4 hover:underline h-10 py-2 px-3 cursor-pointer text-[#ff0000] font-normal"
                    onClick={() => setRowSelections([])}
                  >
                    Désélectionner tout
                  </div>
                </th>
              )}
              {columnVisibility.contributions && (
                <SummaryColumn
                  label={isMobile ? "CO" : "Contributions déjà obtenues"}
                  value={
                    parseFloat(
                      filteredTotals.obtained.toFixed(1)
                    ).toLocaleString("fr-FR") + " €"
                  }
                  color="#9dc95f"
                />
              )}

              {columnVisibility.contributionCall && (
                <SummaryColumn
                  label={isMobile ? "AC" : "Appel à contributions"}
                  value={
                    parseFloat(
                      filteredTotals.contributions.toFixed(1)
                    ).toLocaleString("fr-FR") + " €"
                  }
                />
              )}
              {columnVisibility.treesPlanted && (
                <SummaryColumn
                  label="Arbres plantés"
                  value={filteredTotals.treesPlanted.toLocaleString("fr-FR")}
                />
              )}
              {columnVisibility.regeneratedArea && (
                <SummaryColumn
                  label="Surfaces régénérées"
                  value={filteredTotals.regeneratedAreas.toLocaleString(
                    "fr-FR"
                  )}
                />
              )}
              {columnVisibility.CO2Sequestre && (
                <SummaryColumn
                  label="CO2 Séquestrés"
                  value={filteredTotals.co2Sequestered.toLocaleString("fr-FR")}
                />
              )}
              {columnVisibility.vegetationCover && (
                <SummaryColumn
                  label="Couverture végétale"
                  value={filteredTotals.vegetationCoverage.toLocaleString(
                    "fr-FR"
                  )}
                />
              )}
              {columnVisibility.jobCreation && (
                <SummaryColumn
                  label="Création d'emplois"
                  value={filteredTotals.jobsCreated.toLocaleString("fr-FR")}
                />
              )}
            </tr>
          </thead>
        </table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
};

export default Totals;
